import { useCallback, useReducer, useState, useMemo } from 'react';
import { getTextLocalizer, Localizer } from '../../../locale/useTextLocalizer';
import { ItemLocation } from '../../Menu';
import { TabItem } from '..';
import { TabEditActionType } from '..';
import { TabProps } from '../__internal__';
import { UseTabItemsStateHookParams, UseTabItemsStateHookResult } from '.';

let localize: Localizer;
getTextLocalizer('NovaComponents-Tab-gui-icu').then((localizer) => {
  localize = localizer;
});

type TabEditPayload = {
  reason: TabEditActionType;
  itemIndex: number;
  item: TabItem;
  newLabel: string;
};
type TabUpdatePayload = {
  items: TabItem[];
};
type TabAddPayload = {
  item: TabItem;
  index: number;
};
type TabClosePayload = {
  itemIndex: number;
};

type TabAction =
  | { type: 'Edit'; payload: TabEditPayload }
  | { type: 'Update'; payload: TabUpdatePayload }
  | { type: 'Add'; payload: TabAddPayload }
  | { type: 'Reorder'; payload: { oldIndex: number; newIndex: number } }
  | { type: 'Close'; payload: TabClosePayload }
  | { type: 'CloseAll' };

type EditableTabItem = TabItem & { committedLabel: TabItem['label'] };

export function getDuplicateTabNameValidator(_errorMessage?: string) {
  return (editedItem: TabItem, value: string, items: TabItem[]) => {
    let errorStatus: TabProps['status'] = null;
    let errorMessage: TabProps['message'] = undefined;
    const duplicateLabels = items.filter(
      (tabItem) =>
        tabItem !== editedItem && tabItem.label?.trim().toUpperCase() === value.trim().toUpperCase()
    );
    if (duplicateLabels.length > 0) {
      errorMessage =
        _errorMessage ??
        localize('tab.labelEdit.duplicateLabel.errorMessage.label', {
          label: value,
        });
      errorStatus = 'alert';
    }
    return { status: errorStatus, message: errorMessage };
  };
}

// This hook deals with TabEditing, TabMenu, New Tab features.
export const useTabItemsState = (input: UseTabItemsStateHookParams): UseTabItemsStateHookResult => {
  const [selectedIndex, setSelectedIndex] = useState(() => input.initialSelectedIndex || 0);
  const labelValidator = input.labelValidator ?? getDuplicateTabNameValidator();

  const addAriaControlsAttribute = useCallback(
    (items: TabItem[], selectedIndex: number) => {
      const updatedItems = items.map((item, index) => ({
        ...item,
        committedLabel: item.label,
        ariaControls: index === selectedIndex ? input.tabPanelId : undefined,
      }));

      return updatedItems;
    },
    [input.tabPanelId]
  );

  const [items, dispatch] = useReducer(
    (state: EditableTabItem[], action: TabAction): EditableTabItem[] => {
      switch (action.type) {
        case 'Edit':
          let clone, status, message;
          const { reason, itemIndex, item, newLabel } = action.payload;
          switch (reason) {
            case 'Change':
              clone = state.slice(0);
              ({ status, message } = labelValidator(item, newLabel, state));
              clone[itemIndex] = {
                ...clone[itemIndex],
                message,
                status,
              };
              return clone;
            case 'Commit':
              clone = state.slice(0);
              ({ status, message } = labelValidator?.(item, newLabel, state));
              const shouldRevert = newLabel.trim() === '' || status || message;
              const newValue = shouldRevert ? state[itemIndex].committedLabel : newLabel;
              clone[itemIndex] = {
                ...clone[itemIndex],
                label: newValue,
                committedLabel: newValue,
                message: undefined,
                status: undefined,
              };
              return clone;
            case 'Cancel':
              clone = state.slice(0);
              clone[itemIndex] = {
                ...clone[itemIndex],
                label: state[itemIndex].committedLabel,
                message: undefined,
                status: undefined,
              };
              return clone;
            default:
              return state;
          }
          break;
        case 'Update':
          return action.payload.items.map((item) => ({ ...item, committedLabel: item.label }));
        case 'Add':
          const tabItems = state.slice(0);
          tabItems.splice(action.payload.index, 0, {
            ...action.payload.item,
            committedLabel: action.payload.item.label,
          });
          return tabItems;
        case 'Reorder':
          const tabs = state.slice(0);
          const [removedTab] = tabs.splice(action.payload.oldIndex, 1);
          tabs.splice(action.payload.newIndex, 0, removedTab);
          return tabs;

        case 'Close':
          const tabsArr = state.slice(0);
          tabsArr.splice(action.payload.itemIndex, 1);
          return tabsArr;

        case 'CloseAll':
          return [];

        default:
          return state;
      }
    },
    addAriaControlsAttribute(input.items, selectedIndex)
  );

  function updateTabLabel(
    itemIndex: number,
    item: TabItem,
    newLabel: string,
    type: TabEditActionType
  ) {
    dispatch({ type: 'Edit', payload: { reason: type, itemIndex, item, newLabel } });
  }

  const updateTabItems = useCallback((items: TabItem[]) => {
    dispatch({ type: 'Update', payload: { items } });
  }, []);

  // can be enhanced to handle stateful menus.
  function onTabMenuItemSelect(itemIndex: number, item: TabItem, location: ItemLocation[]) {
    input.onTabMenuItemSelect?.(itemIndex, item, location);
  }

  function onItemSelected(e: { index: number; item: TabItem }) {
    input.onItemSelected?.(e.item);
    setSelectedIndex(e.index);
  }

  function addNewTab(item?: TabItem, index?: number) {
    let newItem: TabItem | void = item;
    if (!newItem) {
      newItem = input.addNewTab?.(items);
    }
    /* -------------------------------------------
     *  Should not accept blank values
     * ---------------------------------------------*/
    if (newItem && newItem.label?.trim() !== '') {
      const newIndex = index ?? items.length;
      newItem && dispatch({ type: 'Add', payload: { item: newItem, index: newIndex } });
      setSelectedIndex(newIndex);
    }
  }

  function reorderTabs(oldIndex: number, newIndex: number) {
    input.onReorder?.(oldIndex, newIndex);
    dispatch({ type: 'Reorder', payload: { oldIndex, newIndex } });
    setSelectedIndex(newIndex);
  }
  const closeTab = (itemIndex: number) => {
    if (itemIndex === selectedIndex && itemIndex === items.length - 1) {
      const newSelectedIndex = items.length - 2 >= 0 ? items.length - 2 : 0;
      setSelectedIndex(newSelectedIndex);
    }
    dispatch({ type: 'Close', payload: { itemIndex } });
  };

  const closeAllTab = () => {
    items.splice(0, items.length);
    dispatch({ type: 'CloseAll' });
  };

  const itemsWithAriaControls = useMemo(() => {
    return addAriaControlsAttribute(items, selectedIndex);
  }, [items, selectedIndex, addAriaControlsAttribute]);

  return {
    selectedIndex,
    setSelectedIndex,
    onItemSelected,
    items: itemsWithAriaControls,
    updateTabItems,
    updateTabLabel,
    onTabMenuItemSelect,
    onTabMenuOpen: input.onTabMenuOpen,
    addNewTab,
    reorderTabs,
    closeTab,
    closeAllTab,
  };
};
